import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';

import { Button } from '@shared/ui/button';
import { Select } from '@shared/ui/select';
import type { SelectOption } from '@shared/ui/select';
import PaginationButtons from '@shared/ui/pagination-buttons';
import { PushCampaignsTable } from '@widgets/push-campaigns-table';
import type { PushPermissions } from '@entities/marketing-push/lib';
import {
  asCount,
  asList,
  marketingPushApi,
  pushError,
} from '../../../api/services/marketingPush.service';
import { CAMPAIGN_STATUS_LABEL } from '../../../types/marketingPushInterfaces';
import type {
  CampaignStatus,
  PushCampaignListItem,
  PushNotificationType,
} from '../../../types/marketingPushInterfaces';

const PAGE_SIZE = 10; // PaginationButtons считает страницы по 10
const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const selectControlStyles = { control: () => ({ height: '44px', minHeight: '44px' }) };

export default function PushCampaignsTab({ permissions }: { permissions: PushPermissions }) {
  const navigate = useNavigate();

  const [types, setTypes] = useState<PushNotificationType[]>([]);
  const [statusFilter, setStatusFilter] = useState<SelectOption | null>(null);
  const [typeFilter, setTypeFilter] = useState<SelectOption | null>(null);
  const [search, setSearch] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<PushCampaignListItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    marketingPushApi
      .getNotificationTypes()
      .then((response) => setTypes(asList(response.data)))
      .catch(() => setTypes([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await marketingPushApi.getCampaigns({
        status: statusFilter ? [statusFilter.value as CampaignStatus] : undefined,
        type: typeFilter ? String(typeFilter.value) : undefined,
        search: search || undefined,
        include_test: includeTest,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setRows(asList(data));
      setCount(asCount(data));
    } catch (loadError) {
      setError(pushError(loadError, 'Не удалось загрузить рассылки'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [includeTest, page, search, statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleDuplicate(campaign: PushCampaignListItem) {
    try {
      const { data } = await marketingPushApi.duplicateCampaign(campaign.id);
      navigate(`/marketing/push/${data.id}`);
    } catch (duplicateError) {
      setError(pushError(duplicateError, 'Не удалось скопировать рассылку'));
    }
  }

  async function handleDelete(campaign: PushCampaignListItem) {
    const confirmed = window.confirm(
      'Убрать рассылку из истории? У пользователей уведомление останется — ' +
        'это удаление только из списка.',
    );
    if (!confirmed) return;
    try {
      await marketingPushApi.deleteCampaign(campaign.id);
      load();
    } catch (deleteError) {
      setError(pushError(deleteError, 'Не удалось удалить рассылку'));
    }
  }

  const statusOptions: SelectOption[] = Object.entries(CAMPAIGN_STATUS_LABEL).map(
    ([value, meta]) => ({ value, label: meta.label }),
  );
  const typeOptions: SelectOption[] = types.map((type) => ({
    value: type.code,
    label: type.name,
  }));

  return (
    <>
      <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className={FIELD_LABEL}>Поиск</label>
            <input
              value={search}
              placeholder="По тексту уведомления"
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
            />
          </div>

          <div className="w-[220px]">
            <label className={FIELD_LABEL}>Статус</label>
            <Select
              placeholder="Любой"
              options={statusOptions}
              optionSelected={statusFilter}
              isVisible={false}
              isClearable
              customStyles={selectControlStyles}
              onSelected={(option: SelectOption | null) => {
                setPage(1);
                setStatusFilter(option);
              }}
            />
          </div>

          <div className="w-[220px]">
            <label className={FIELD_LABEL}>Тип</label>
            <Select
              placeholder="Любой"
              options={typeOptions}
              optionSelected={typeFilter}
              isVisible={false}
              isClearable
              customStyles={selectControlStyles}
              onSelected={(option: SelectOption | null) => {
                setPage(1);
                setTypeFilter(option);
              }}
            />
          </div>

          <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm text-[#2d2c2c]">
            <input
              type="checkbox"
              checked={includeTest}
              onChange={() => {
                setPage(1);
                setIncludeTest((flag) => !flag);
              }}
            />
            Показывать тестовые
          </label>

          {permissions.create && (
            <div className="ml-auto mb-3">
              <Button onClick={() => navigate('/marketing/push/new')}>
                <span className="inline-flex items-center gap-1">
                  <FiPlus /> Новая рассылка
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-xl bg-white shadow-sm">
        <PushCampaignsTable
          rows={rows}
          loading={loading}
          onRowClick={(campaign) => navigate(`/marketing/push/${campaign.id}`)}
          onReport={(campaign) => navigate(`/marketing/push/${campaign.id}/report`)}
          onDuplicate={permissions.create ? handleDuplicate : undefined}
          onDelete={permissions.remove ? handleDelete : undefined}
        />
      </div>

      {count > PAGE_SIZE && (
        <div className="mt-4">
          <PaginationButtons
            currentPage={page}
            setCurrentPage={setPage}
            ordersLength={count}
          />
        </div>
      )}
    </>
  );
}
